import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { UpdateLeadData } from '../../types/lead';
import Button from '../UI/Button';

const toDatetimeLocalValue = (iso?: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export interface LeadEditModalInitial {
  fullName?: string | null;
  email?: string | null;
  companyName?: string | null;
  followUpDate?: string | null;
  status?: string;
  notes?: string | null;
}

interface LeadEditModalProps {
  isOpen: boolean;
  leadId: string;
  initial: LeadEditModalInitial;
  onClose: () => void;
  onSaved: () => void;
}

const LeadEditModal: React.FC<LeadEditModalProps> = ({
  isOpen,
  leadId,
  initial,
  onClose,
  onSaved,
}) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [followUpAt, setFollowUpAt] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const showFollowUp =
    Boolean(initial.followUpDate) || initial.status === 'follow_up';

  useEffect(() => {
    if (!isOpen) return;
    setFullName((initial.fullName ?? '').trim());
    setEmail((initial.email ?? '').trim());
    setCompanyName((initial.companyName ?? '').trim());
    setFollowUpAt(toDatetimeLocalValue(initial.followUpDate));
    setNotes((initial.notes ?? '').trim());
  }, [isOpen, initial]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    const trimmedName = fullName.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const patch: UpdateLeadData = {
        fullName: trimmedName,
        email: email.trim() || undefined,
        companyName: companyName.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      if (showFollowUp && followUpAt) {
        patch.followUpDate = new Date(followUpAt).toISOString();
      } else if (showFollowUp && !followUpAt && initial.followUpDate) {
        patch.followUpDate = null;
      }

      await leadService.updateLead(leadId, patch);
      toast.success('Lead updated');
      onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close editor"
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-edit-title"
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <h3 id="lead-edit-title" className="text-lg font-semibold text-foreground">
            Edit lead
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="scroll-area space-y-4 overflow-y-auto p-6">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Name <span className="text-destructive">*</span>
            </span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              autoFocus
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Company
            </span>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </label>

          {showFollowUp && (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Follow-up date & time
              </span>
              <input
                type="datetime-local"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-xs text-muted-foreground">
                Clear the date to remove a scheduled follow-up.
              </p>
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add notes about this lead…"
              className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </label>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outlined" size="sm" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving}>
            Save changes
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LeadEditModal;
